/**
 * PredictiveDialContext — orchestrates DialIQ Predictive Dial sessions.
 *
 * Responsibilities:
 *   1. Lifecycle: start/end the backend session, manage persistent conference name
 *   2. Stats sidebar data: poll backend every few seconds for live counters
 *   3. Pre-dial trigger: while the agent is on a call, decide when to silently
 *      dial the next lead so they're already in a holding conference when the
 *      agent disposes their current call
 *   4. Bridge/abandon: when the agent disposes, either bridge the pre-dialed
 *      customer into the agent's conference (success) or play the TCPA callback
 *      message (abandoned)
 *   5. Auto-throttle: respect the backend's currentPredialPace=0 signal and
 *      stop pre-dialing if abandonment crossed kill threshold
 *
 * This context is OPTIONAL. The PowerDialer works without it. When predictive
 * is active for the session, PowerDialerSessionLogic reads predial state from
 * this context to skip the standard dial step (the customer is already
 * connected via predial).
 *
 * Boundaries:
 *   - This context does NOT control the agent SDK directly — TwilioDeviceContext
 *     owns that. We just track the agent's current call state.
 *   - This context does NOT manage the lead queue — PowerDialerSessionLogic does.
 *     We just peek at "what's next" via a getNextLead callback.
 */
"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import predictiveDialService, {
  type PredictiveAgentStats,
  type PredictiveSessionStats,
} from "@/app/_module/services/predictiveDialService";
import { type Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useVoiceDeviceContext, getCallSid } from "@/app/_module/contexts/VoiceDeviceProvider";

export type PredialState =
  | "idle"            // not currently pre-dialing
  | "dialing"         // outbound dial in progress
  | "ringing"         // customer phone ringing
  | "holding"         // customer answered, on hold music
  | "bridging"        // moving customer into agent conference
  | "bridged"         // successfully bridged (transient — soon back to idle)
  | "abandoned";      // played callback message, hung up

export interface PredictiveDialContextValue {
  /** True when a predictive session is active. */
  isPredictiveActive: boolean;
  /** Live session ID (backend). */
  sessionId: string | null;
  /** Persistent agent conference name — agent SDK joins this once per session. */
  conferenceName: string | null;
  /** Snapshot of the agent's stats at session start + live updates. */
  agentStats: PredictiveAgentStats | null;
  /** Live session counters refreshed every 5s. */
  liveStats: PredictiveSessionStats | null;

  /** Pre-dial pace from the backend: 0=disabled, 1=standard, 2=aggressive. */
  predialPace: number;

  /** Current pre-dial state (the lead being pre-dialed, if any). */
  predialState: PredialState;
  predialLead: Lead | null;
  predialCustomerCallSid: string | null;
  predialHoldingConference: string | null;

  /** Whether abandonment auto-throttle has fully killed predictive for this session. */
  isThrottleKilled: boolean;

  // ─── Lifecycle ─────────────────────────────────────────────────────────────
  startSession: (source?: "dialiq" | "manual_power") => Promise<{
    sessionId: string;
    conferenceName: string;
  } | null>;
  endSession: (reason?: "agent_stopped" | "completed") => Promise<void>;

  // ─── Hooks for PowerDialerSessionLogic ─────────────────────────────────────
  /** Call when the agent's current call ENDS (disposition click). Bridges the
   *  pre-dialed customer if available, otherwise no-op. When bridged:true,
   *  customerCallSid is the SID of the now-connected customer so the caller
   *  can mark the lead as pre-bridged and skip the next handleCall's dial. */
  handleAgentCallEnded: () => Promise<{ bridged: boolean; lead: Lead | null; customerCallSid: string | null }>;

  /** Call periodically (e.g. every 5s) with the elapsed time of the current
   *  agent call. The context decides if/when to pre-dial the next lead. */
  notifyCallElapsed: (currentCallElapsedMs: number, getNextLead: () => Lead | null, fromNumber: string) => Promise<void>;

  // ─── Persistent-conference primary dial ────────────────────────────────────
  /** Dial a lead directly into the agent's persistent conference. Used by
   *  PowerDialerSessionLogic.handleCall when predictive is active — the agent
   *  is already in the conference, customer is added to it. Returns the
   *  customer's Twilio call SID for later end-customer-leg. */
  dialLead: (lead: Lead, fromNumber: string) => Promise<{ customerCallSid: string } | null>;

  /** End a single customer's call leg without yanking the agent out of the
   *  persistent conference. Used by handleEndCall in predictive mode. */
  endCustomerLeg: (customerCallSid: string) => Promise<void>;

  /** True if the agent's SDK is currently connected to the persistent
   *  conference. Frontend can use this to gate behavior on "agent is wired up". */
  isAgentInConference: boolean;
}

const PredictiveDialContext = createContext<PredictiveDialContextValue | null>(null);

export function PredictiveDialProvider({ children }: { children: React.ReactNode }) {
  const { toast } = useToast();
  const twilioDevice = useVoiceDeviceContext();

  // Session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [conferenceName, setConferenceName] = useState<string | null>(null);
  const [agentStats, setAgentStats] = useState<PredictiveAgentStats | null>(null);
  const [liveStats, setLiveStats] = useState<PredictiveSessionStats | null>(null);
  const [predialPace, setPredialPace] = useState<number>(1);
  const [isThrottleKilled, setIsThrottleKilled] = useState(false);
  const [isAgentInConference, setIsAgentInConference] = useState(false);
  const agentConferenceCallRef = useRef<any>(null);

  // Pre-dial state
  const [predialState, setPredialState] = useState<PredialState>("idle");
  const [predialLead, setPredialLead] = useState<Lead | null>(null);
  const [predialCustomerCallSid, setPredialCustomerCallSid] = useState<string | null>(null);
  const [predialHoldingConference, setPredialHoldingConference] = useState<string | null>(null);

  // Refs to avoid stale closures in async polling callbacks
  const sessionIdRef = useRef<string | null>(null);
  const predialStateRef = useRef<PredialState>("idle");
  const predialCustomerCallSidRef = useRef<string | null>(null);
  const predialHoldingConferenceRef = useRef<string | null>(null);
  const lastPredialTriggerCheckRef = useRef<number>(0);

  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { predialStateRef.current = predialState; }, [predialState]);
  useEffect(() => { predialCustomerCallSidRef.current = predialCustomerCallSid; }, [predialCustomerCallSid]);
  useEffect(() => { predialHoldingConferenceRef.current = predialHoldingConference; }, [predialHoldingConference]);

  const isPredictiveActive = !!sessionId;

  // ─── Stats polling (every 5s while session is active) ──────────────────────
  useEffect(() => {
    if (!sessionId) return;

    const poll = async () => {
      try {
        const stats = await predictiveDialService.getStats(sessionId);
        setLiveStats(stats);
        setPredialPace(stats.currentPredialPace);
        if (stats.currentPredialPace === 0 && !isThrottleKilled) {
          setIsThrottleKilled(true);
          toast({
            title: "Predictive throttled",
            description: `Abandonment rate ${(stats.abandonmentRate * 100).toFixed(1)}% — predictive disabled for the rest of this session.`,
            variant: "destructive",
            duration: 8000,
          });
        }
      } catch (err) {
        // Silently swallow — stats polling is non-critical.
        console.warn("[PredictiveDial] stats poll failed:", err);
      }
    };

    poll(); // immediate
    const intervalId = setInterval(poll, 5000);
    return () => clearInterval(intervalId);
  }, [sessionId, isThrottleKilled, toast]);

  // ─── Lifecycle ──────────────────────────────────────────────────────────────
  const startSession = useCallback(
    async (source: "dialiq" | "manual_power" = "dialiq") => {
      try {
        const session = await predictiveDialService.startSession(source);
        // Backend returns sessionId=null when predictive is globally disabled
        // via PREDICTIVE_DIAL_ENABLED env var. Silently fall back — no toast,
        // standard PowerDialer runs as if predictive wasn't there.
        if (!session.sessionId) {
          console.log("[PredictiveDial] backend reports disabled — using standard PowerDialer");
          return null;
        }
        setSessionId(session.sessionId);
        setConferenceName(session.conferenceName);
        setAgentStats(session.agentStats);
        setPredialPace(session.currentPredialPace);
        setIsThrottleKilled(false);
        setPredialState("idle");
        setPredialLead(null);
        setPredialCustomerCallSid(null);
        setPredialHoldingConference(null);

        // Connect the agent's SDK to the persistent conference. They stay
        // here for the entire session — every customer is added to this
        // conference instead of creating a new per-call one.
        if (session.conferenceName) {
          try {
            const agentCall = await twilioDevice.connect({
              to: `predictive:${session.conferenceName}`,
              metadata: { predictiveSessionId: session.sessionId },
            });
            agentConferenceCallRef.current = agentCall;
            // Register the conference name with TwilioDeviceContext so
            // addParticipantToCall (three-way) targets the right conference.
            // The connect() helper only auto-sets this for "conference:" prefix.
            twilioDevice.setActiveConference(session.conferenceName);
            setIsAgentInConference(true);
            console.log("[PredictiveDial] agent connected to conference", {
              sessionId: session.sessionId,
              conferenceName: session.conferenceName,
            });
          } catch (connErr) {
            // Conference join failed — predictive is broken for this session
            // but we can still let the agent fall back to standard PowerDialer.
            console.warn("[PredictiveDial] failed to join conference, falling back:", connErr);
            // Tear down the backend session so we don't leak state.
            await predictiveDialService.endSession(session.sessionId, "error").catch(() => {});
            setSessionId(null);
            setConferenceName(null);
            return null;
          }
        }

        return {
          sessionId: session.sessionId,
          // Backend guarantees conferenceName is non-null when sessionId is set;
          // the early-return above handles the disabled-response case.
          conferenceName: session.conferenceName!,
        };
      } catch (err) {
        // Real network/backend error — log but stay silent for the agent.
        // Standard PowerDialer continues to work without predictive.
        console.warn("[PredictiveDial] start session failed, falling back silently:", err);
        return null;
      }
    },
    [],
  );

  const endSession = useCallback(
    async (reason: "agent_stopped" | "completed" = "agent_stopped") => {
      const sid = sessionIdRef.current;
      if (!sid) return;

      // Disconnect the agent's SDK from the persistent conference FIRST so
      // they stop hearing hold music + free up the device for the next session.
      if (agentConferenceCallRef.current) {
        try {
          twilioDevice.disconnect({ userInitiated: true });
        } catch (discErr) {
          console.warn("[PredictiveDial] agent disconnect failed:", discErr);
        }
        agentConferenceCallRef.current = null;
      }
      // Clear the conference registration so three-way + other lookups
      // don't keep pointing at a dead conference.
      try { twilioDevice.setActiveConference(null); } catch { /* noop */ }
      setIsAgentInConference(false);

      try {
        await predictiveDialService.endSession(sid, reason);
      } catch (err) {
        console.warn("[PredictiveDial] end session failed (continuing):", err);
      }
      setSessionId(null);
      setConferenceName(null);
      setAgentStats(null);
      setLiveStats(null);
      setPredialState("idle");
      setPredialLead(null);
      setPredialCustomerCallSid(null);
      setPredialHoldingConference(null);
      setIsThrottleKilled(false);
    },
    [twilioDevice],
  );

  // ─── Hooks for PowerDialerSessionLogic ──────────────────────────────────────
  /**
   * Agent's current call just ended (disposition click). If we have a
   * pre-dialed customer in the holding conference, bridge them into the agent
   * conference. Otherwise no-op (PowerDialer falls back to standard next-dial).
   */
  const handleAgentCallEnded = useCallback(async (): Promise<{ bridged: boolean; lead: Lead | null; customerCallSid: string | null }> => {
    const sid = sessionIdRef.current;
    const callSid = predialCustomerCallSidRef.current;
    const holdConf = predialHoldingConferenceRef.current;
    const state = predialStateRef.current;
    const lead = predialLead;

    if (!sid || !callSid || !holdConf || state !== "holding") {
      // No customer ready to bridge.
      return { bridged: false, lead: null, customerCallSid: null };
    }

    setPredialState("bridging");
    try {
      await predictiveDialService.bridge(sid, {
        customerCallSid: callSid,
        holdingConference: holdConf,
        agentParentCallSid: getCallSid(agentConferenceCallRef.current) ?? undefined,
      });
      setPredialState("bridged");
      // Clear pre-dial buffer — the next call begins
      setTimeout(() => {
        setPredialState("idle");
        setPredialLead(null);
        setPredialCustomerCallSid(null);
        setPredialHoldingConference(null);
      }, 300);
      return { bridged: true, lead, customerCallSid: callSid };
    } catch (err) {
      console.error("[PredictiveDial] bridge failed:", err);
      // Fall back to abandon flow — play "we'll call you back" message
      try {
        await predictiveDialService.abandon(sid, {
          customerCallSid: callSid,
          leadId: lead?.id ?? "",
        });
      } catch { /* swallow */ }
      setPredialState("abandoned");
      setTimeout(() => {
        setPredialState("idle");
        setPredialLead(null);
        setPredialCustomerCallSid(null);
        setPredialHoldingConference(null);
      }, 300);
      return { bridged: false, lead: null, customerCallSid: null };
    }
  }, [predialLead]);

  /**
   * Called periodically by PowerDialerSessionLogic. Decides whether to
   * pre-dial the next lead right now. Idempotent — if already pre-dialing,
   * does nothing.
   */
  const notifyCallElapsed = useCallback(
    async (currentCallElapsedMs: number, getNextLead: () => Lead | null, fromNumber: string) => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      if (predialStateRef.current !== "idle") return; // already pre-dialing
      if (predialPace === 0) return; // throttled off

      // Rate-limit decision checks — ask the backend at most once every 3 seconds.
      const now = Date.now();
      if (now - lastPredialTriggerCheckRef.current < 3000) return;
      lastPredialTriggerCheckRef.current = now;

      let decision;
      try {
        decision = await predictiveDialService.shouldPredial(sid, currentCallElapsedMs);
      } catch (err) {
        console.warn("[PredictiveDial] shouldPredial check failed:", err);
        return;
      }
      if (!decision.should) return;

      const nextLead = getNextLead();
      if (!nextLead || !nextLead.phone) {
        // No next lead — nothing to pre-dial.
        return;
      }

      setPredialState("dialing");
      setPredialLead(nextLead);
      try {
        const result = await predictiveDialService.predial(sid, {
          leadId: nextLead.id,
          toNumber: nextLead.phone,
          fromNumber,
        });
        if (result.skipped) {
          // Backend declined (e.g. throttle). Reset.
          setPredialState("idle");
          setPredialLead(null);
          return;
        }
        setPredialCustomerCallSid(result.customerCallSid ?? null);
        setPredialHoldingConference(result.holdingConference ?? null);
        // Optimistically advance state. The customer-status webhook on the
        // backend will eventually confirm/deny, but we want UI to update fast.
        setPredialState("ringing");

        // After ~10s of ringing assume they answered + are on hold music.
        // (Real impl: subscribe to customer status broadcasts to flip state
        // precisely. This is a reasonable fallback for now.)
        setTimeout(() => {
          if (predialStateRef.current === "ringing") {
            setPredialState("holding");
          }
        }, 10_000);
      } catch (err) {
        console.error("[PredictiveDial] predial dispatch failed:", err);
        setPredialState("idle");
        setPredialLead(null);
        setPredialCustomerCallSid(null);
        setPredialHoldingConference(null);
      }
    },
    [predialPace],
  );

  // ─── Persistent-conference primary dial ─────────────────────────────────────
  // PowerDialerSessionLogic.handleCall calls this when predictive is active.
  // The agent is already in the persistent conference; we just need the
  // backend to dial the customer into the same conference. We also play
  // ringback locally so the agent hears "rrring..." while the customer's
  // phone rings (the conference itself is silent until customer joins).
  const dialLead = useCallback(
    async (lead: Lead, fromNumber: string): Promise<{ customerCallSid: string } | null> => {
      const sid = sessionIdRef.current;
      if (!sid) {
        console.warn("[PredictiveDial] dialLead called with no active session");
        return null;
      }
      if (!lead.phone) {
        console.warn("[PredictiveDial] dialLead called with no phone");
        return null;
      }

      // Start ringback BEFORE the API call so the agent hears feedback
      // immediately. The existing volume handler on the agent's conference
      // call (TwilioDeviceContext) auto-stops it when the customer's audio
      // joins the conference. Backed up by the customer_answered Supabase
      // realtime event and a 30s hard timeout.
      try { twilioDevice.playRingback(); } catch { /* noop */ }

      // Agent's SDK call SID — needed by backend to register the DTMF cache
      // mapping (InsuraBot key-grab) and attach a Media Stream for monitoring.
      // The Twilio SDK exposes it as call.parameters.CallSid after acceptance;
      // provider-neutral getCallSid resolves the same value (Telnyx: call.id).
      const agentParentCallSid: string | undefined =
        getCallSid(agentConferenceCallRef.current) ?? undefined;

      try {
        const result = await predictiveDialService.dialLead(sid, {
          leadId: lead.id,
          toNumber: lead.phone,
          fromNumber,
          agentParentCallSid,
        });
        return { customerCallSid: result.customerCallSid };
      } catch (err) {
        console.error("[PredictiveDial] dialLead failed:", err);
        // Stop ringback on dial failure — no customer is coming.
        try { twilioDevice.stopRingback(); } catch { /* noop */ }
        return null;
      }
    },
    [twilioDevice],
  );

  // End just the customer's leg without yanking the agent out of the
  // persistent conference. Agent stays in to receive the next bridge/dial.
  const endCustomerLeg = useCallback(
    async (customerCallSid: string): Promise<void> => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      // Safety: kill any in-flight ringback. If agent ended the call before
      // the customer answered, the volume handler won't fire (no audio ever
      // came through), so ringback would otherwise run to its 30s timeout.
      try { twilioDevice.stopRingback(); } catch { /* noop */ }
      try {
        await predictiveDialService.endCustomerLeg(sid, { customerCallSid });
      } catch (err) {
        // Best-effort — Twilio may have already ended the call. The
        // backend endpoint swallows 21220, so any error here is unexpected.
        console.warn("[PredictiveDial] endCustomerLeg failed:", err);
      }
    },
    [twilioDevice],
  );

  // ─── Cleanup on unmount ─────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      // If component unmounts mid-session (e.g. nav away), end gracefully.
      const sid = sessionIdRef.current;
      if (sid) {
        predictiveDialService.endSession(sid, "agent_stopped").catch(() => {});
      }
    };
  }, []);

  const value: PredictiveDialContextValue = {
    isPredictiveActive,
    sessionId,
    conferenceName,
    agentStats,
    liveStats,
    predialPace,
    predialState,
    predialLead,
    predialCustomerCallSid,
    predialHoldingConference,
    isThrottleKilled,
    startSession,
    endSession,
    handleAgentCallEnded,
    notifyCallElapsed,
    dialLead,
    endCustomerLeg,
    isAgentInConference,
  };

  return (
    <PredictiveDialContext.Provider value={value}>
      {children}
    </PredictiveDialContext.Provider>
  );
}

export function usePredictiveDial(): PredictiveDialContextValue {
  const ctx = useContext(PredictiveDialContext);
  if (!ctx) {
    // Safe no-op fallback — lets components call usePredictiveDial unconditionally
    // even when the provider isn't mounted (e.g. on legacy pages).
    return {
      isPredictiveActive: false,
      sessionId: null,
      conferenceName: null,
      agentStats: null,
      liveStats: null,
      predialPace: 0,
      predialState: "idle",
      predialLead: null,
      predialCustomerCallSid: null,
      predialHoldingConference: null,
      isThrottleKilled: false,
      startSession: async () => null,
      endSession: async () => {},
      handleAgentCallEnded: async () => ({ bridged: false, lead: null, customerCallSid: null }),
      notifyCallElapsed: async () => {},
      dialLead: async () => null,
      endCustomerLeg: async () => {},
      isAgentInConference: false,
    };
  }
  return ctx;
}
